/**
 * TanStack Query hooks for the admin feature-flags surface (STORY-008-009).
 *
 * `useFlags`  — `useQuery` over the flat list (no pagination server-side).
 *   Accepts an optional `enabled` so a caller that may not have permission to
 *   read flags (the Guest access form, W3.1) can skip the request entirely
 *   rather than firing it and eating a 403.
 * `useFlag`   — `useQuery` for one flag (edit prefill; optional).
 * `useCreateFlag` / `useUpdateFlag` / `useDeleteFlag` — mutations that
 *   invalidate the `["admin","feature-flags"]` root on success so the list
 *   refetches after the backend publishes `feature_flags.changed`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  createFlag,
  deleteFlag,
  flagKeys,
  getFlag,
  listFlags,
  updateFlag,
  type AdminFeatureFlag,
  type FeatureFlagCreateBody,
  type FeatureFlagListResponse,
  type FeatureFlagUpdateBody,
} from "@/features/admin/feature-flags/api";

export function useFlags(
  options: { enabled?: boolean } = {},
): UseQueryResult<FeatureFlagListResponse, Error> {
  return useQuery<FeatureFlagListResponse, Error>({
    queryKey: flagKeys.list(),
    queryFn: listFlags,
    staleTime: 10_000,
    // The Guest access form (W3.1) reads flags only for an admin who may
    // manage them; every existing caller keeps the default.
    enabled: options.enabled ?? true,
  });
}

export function useFlag(
  key: string | null,
): UseQueryResult<AdminFeatureFlag, Error> {
  return useQuery<AdminFeatureFlag, Error>({
    queryKey: flagKeys.detail(key ?? ""),
    queryFn: () => getFlag(key!),
    enabled: key !== null,
    staleTime: 0,
  });
}

function useInvalidateFlags(): () => Promise<void> {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: flagKeys.all });
}

export function useCreateFlag(): UseMutationResult<
  AdminFeatureFlag,
  Error,
  FeatureFlagCreateBody
> {
  const invalidate = useInvalidateFlags();
  return useMutation<AdminFeatureFlag, Error, FeatureFlagCreateBody>({
    mutationFn: (body) => createFlag(body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateFlag(): UseMutationResult<
  AdminFeatureFlag,
  Error,
  { key: string; body: FeatureFlagUpdateBody }
> {
  const invalidate = useInvalidateFlags();
  return useMutation<
    AdminFeatureFlag,
    Error,
    { key: string; body: FeatureFlagUpdateBody }
  >({
    mutationFn: ({ key, body }) => updateFlag(key, body),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteFlag(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateFlags();
  return useMutation<void, Error, string>({
    mutationFn: (key) => deleteFlag(key),
    onSuccess: () => invalidate(),
  });
}
